import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { FileText, CheckCircle, Download} from 'lucide-react';

export const ComplianceReportsPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompliance, setFilterCompliance] = useState('All');

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) setDevices(r.data);
      setLoading(false);
    });
  }, []);

  const filtered = devices.filter(d => filterCompliance === 'All' || d.complianceStatus === filterCompliance);

  const totalCompliance = devices.length > 0 ? Math.round((devices.filter(d => d.complianceStatus === 'Compliant').length / devices.length) * 100) : 0;

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#6366f120', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    btnSecond: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    topGrid: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '16px', marginBottom: '22px' },
    scoreCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
    scoreNum: { fontSize: '64px', fontWeight: '800', lineHeight: 1 },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
    statCard: { backgroundColor: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '4px' },
    statVal: { fontSize: '22px', fontWeight: '700' },
    filterBar: { display: 'flex', gap: '10px', marginBottom: '16px' },
    select: { padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
    checkRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    checkItem: { fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '6px' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><FileText size={22} color="#6366f1" /></div>
          <div><h1 style={s.title}>Compliance Reports</h1><div style={s.subtitle}>Device compliance overview and detailed reports</div></div>
        </div>
        <button style={s.btnSecond}><Download size={15} /> Export Report</button>
      </div>

      <div style={s.topGrid}>
        <div style={s.scoreCard}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Compliance Score</div>
          <div style={{ ...s.scoreNum, color: totalCompliance >= 80 ? '#10b981' : totalCompliance >= 60 ? '#f59e0b' : '#ef4444' }}>{totalCompliance}%</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>{devices.filter(d => d.complianceStatus === 'Compliant').length} of {devices.length} devices</div>
          <div style={{ width: '100%', height: '8px', borderRadius: '4px', backgroundColor: '#e2e8f0', marginTop: '14px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${totalCompliance}%`, backgroundColor: totalCompliance >= 80 ? '#10b981' : '#f59e0b', borderRadius: '4px', transition: 'width 0.5s' }} />
          </div>
        </div>
        <div style={s.statsRow}>
          {[['Compliant', devices.filter(d => d.complianceStatus === 'Compliant').length, '#10b981'], ['Non-Compliant', devices.filter(d => d.complianceStatus === 'Non-Compliant').length, '#ef4444'], ['Unknown', devices.filter(d => d.complianceStatus === 'Unknown').length, '#f59e0b']].map(([l, v, c]) => (
            <div key={l} style={{ ...s.statCard, borderLeft: `3px solid ${c}` }}><div style={s.statLabel}>{l}</div><div style={{ ...s.statVal, color: c }}>{v}</div></div>
          ))}
        </div>
      </div>

      <div style={s.filterBar}>
        <select style={s.select} value={filterCompliance} onChange={e => setFilterCompliance(e.target.value)}>
          <option value="All">All Devices</option><option value="Compliant">Compliant</option><option value="Non-Compliant">Non-Compliant</option><option value="Unknown">Unknown</option>
        </select>
      </div>

      <div style={s.card}>
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading compliance data...</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Device</th>
                <th style={s.th}>User</th>
                <th style={s.th}>Platform</th>
                <th style={s.th}>Compliance</th>
                <th style={s.th}>Checks</th>
                <th style={s.th}>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(dev => (
                <tr key={dev.id}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={s.td}><div style={{ fontWeight: '500' }}>{dev.deviceName}</div><div style={{ fontSize: '11px', color: '#64748b' }}>{dev.serialNumber}</div></td>
                  <td style={s.td}>{dev.assignedTo || '—'}</td>
                  <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getPlatformColor()}15`, color: dev.getPlatformColor() }}>{dev.platform}</span></td>
                  <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getComplianceColor()}15`, color: dev.getComplianceColor() }}>{dev.complianceStatus}</span></td>
                  <td style={s.td}>
                    <div style={s.checkRow}>
                      <span style={{ ...s.checkItem, backgroundColor: dev.isEncrypted ? '#10b98115' : '#ef444415', color: dev.isEncrypted ? '#10b981' : '#ef4444' }}>
                        {dev.isEncrypted ? '✓' : '✗'} Encrypted
                      </span>
                      <span style={{ ...s.checkItem, backgroundColor: !dev.isLocked ? '#10b98115' : '#f59e0b15', color: !dev.isLocked ? '#10b981' : '#f59e0b' }}>
                        {!dev.isLocked ? '✓' : '!'} Lock
                      </span>
                      <span style={{ ...s.checkItem, backgroundColor: dev.batteryLevel > 20 ? '#10b98115' : '#ef444415', color: dev.batteryLevel > 20 ? '#10b981' : '#ef4444' }}>
                        {dev.batteryLevel > 20 ? '✓' : '✗'} Battery
                      </span>
                    </div>
                  </td>
                  <td style={s.td}><span style={{ fontSize: '12px', color: dev.getDaysSinceLastSeen() > 7 ? '#ef4444' : '#64748b' }}>{dev.getDaysSinceLastSeen()}d ago</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
export default ComplianceReportsPage;